import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild, } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Competency, CompetencyStatus, isPendingLikeStatus, UserCompetency } from 'app/_models/competency.model';
import { CompetencyService } from 'app/_services/competency.service';
import { ContractorService } from 'app/_services/contractor.service';
import { NotificationService } from 'app/_services/notification.service';
import DateTimeUtils from 'app/_utils/datetime.utils';
import { AcceptCompetencyModalComponent } from 'app/components/accept-competency-modal/accept-competency-modal.component';
import { RejectCompetencyModalComponent } from 'app/components/reject-competency-modal/reject-competency-modal.component';
import { UploadEvidenceModalComponent } from 'app/components/upload-evidence-modal/upload-evidence-modal.component';
import { environment } from 'environments/environment';
import Swal from 'sweetalert2';

type EvidenceState = 'document' | 'upload' | 'requested' | 'request' | 'none';
export type CompetencyCardType =
  'user-edit'
  | 'user-draft'
  | 'user-readonly'
  | 'user-self'
  | 'work-order-edit'
  | 'work-order-readonly';

@Component({
  selector: 'competency-card',
  templateUrl: './competency-card.component.html',
})
export class CompetencyCardComponent implements AfterViewChecked {
  @Input() competency: Competency;
  @Input() cardType: CompetencyCardType = 'work-order-readonly';
  @Input() disabled = false; // Set to true only while an action is in-flight (not for permission gating). e.g. avoid double clicking
  @Input() userCanManageCompetencies = false;
  @Input() workOrderStateAllowsDelete = true;
  @Input() siteOwnerId: number;
  @Output() updated = new EventEmitter<Competency>();
  @Output() delete = new EventEmitter<Competency>();
  @Output() evidenceRequested = new EventEmitter<Competency>();

  @ViewChild('modelValue') modelValueRef: ElementRef<HTMLElement>;
  @ViewChild('ratingValue') ratingValueRef: ElementRef<HTMLElement>;

  isModelExpanded = false;
  isRatingExpanded = false;
  isModelOverflowing = false;
  isRatingOverflowing = false;

  private statusMeta: { [key: string]: { prefix: string; className: string } } = {
    [CompetencyStatus.Approved]: { prefix: 'Valid until', className: 'competency-card__status--valid' },
    [CompetencyStatus.Expired]: { prefix: 'Expired', className: 'competency-card__status--expired' },
    [CompetencyStatus.Unverified]: { prefix: 'Unverified', className: 'competency-card__status--unverified' },
    [CompetencyStatus.DocumentRequested]: { prefix: 'Evidence Requested', className: 'competency-card__status--unverified' },
    [CompetencyStatus.DocumentSubmitted]: { prefix: 'Submitted for Approval', className: 'competency-card__status--unverified' },
    [CompetencyStatus.Rejected]: { prefix: 'Rejected', className: 'competency-card__status--rejected' },
  };
  isRequestingEvidence = false;

  constructor(
    private competencyService: CompetencyService,
    private contractorService: ContractorService,
    private notificationService: NotificationService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewChecked(): void {
    let changed = false;

    if (!this.isModelExpanded) {
      const modelOverflow = this.isElementOverflowing(this.modelValueRef);
      if (modelOverflow !== this.isModelOverflowing) {
        this.isModelOverflowing = modelOverflow;
        changed = true;
      }
    }

    if (!this.isRatingExpanded) {
      const ratingOverflow = this.isElementOverflowing(this.ratingValueRef);
      if (ratingOverflow !== this.isRatingOverflowing) {
        this.isRatingOverflowing = ratingOverflow;
        changed = true;
      }
    }

    if (changed) {
      this.cdr.detectChanges();
    }
  }

  toggleModel(): void {
    this.isModelExpanded = !this.isModelExpanded;
  }

  toggleRating(): void {
    this.isRatingExpanded = !this.isRatingExpanded;
  }

  private isElementOverflowing(ref: ElementRef<HTMLElement> | undefined): boolean {
    if (!ref || !ref.nativeElement) {
      return false;
    }
    const el = ref.nativeElement;
    return el.scrollHeight > el.clientHeight + 1;
  }

  get headerColor(): string {
    const typeKey = this.normalizeKey(this.competency.type || '');
    if (!typeKey) {
      return 'var(--skylark-blue)';
    }

    const categoryPalette: { [key: string]: string } = {
      turbine: 'var(--skylark-competency-turbine)',
      site: 'var(--skylark-competency-site)',
    };

    return categoryPalette[typeKey] || 'var(--skylark-blue)';
  }

  get statusPrefix(): string {
    const meta = this.statusMeta[this.statusValue];
    return meta ? meta.prefix : '';
  }

  get statusClass(): string {
    const meta = this.statusMeta[this.statusValue];
    return meta ? meta.className : '';
  }

  get canAccept(): boolean {
    return this.canManageUserEditCard
      && (isPendingLikeStatus(this.statusValue)
        || this.statusValue === CompetencyStatus.Rejected
        || this.statusValue === CompetencyStatus.Expired);
  }

  get canReject(): boolean {
    return this.canManageUserEditCard
      && this.statusValue === CompetencyStatus.DocumentSubmitted;
  }

  get canDelete(): boolean {
    if (!this.allowsDeleteControl) {
      return false;
    }

    if (this.isDraftCard) {
      return true;
    }

    if (this.cardType === 'work-order-edit') {
      return this.workOrderStateAllowsDelete;
    }

    if (this.isUserCard) {
      return this.canManageUserEditCard && (this.competency as UserCompetency).hasCompetencyApproval;
    }

    return false;
  }

  get statusValue(): CompetencyStatus {
    return this.isUserCard ? (this.competency as UserCompetency).status : CompetencyStatus.Unverified;
  }

  get expiryDate(): string | Date | undefined {
    return this.isUserCard ? (this.competency as UserCompetency).expiryDate : undefined;
  }

  get documentFileName(): string | undefined {
    return this.isUserCard ? (this.competency as UserCompetency).documentFileName : undefined;
  }

  get shouldShowAtCtInfoTooltip(): boolean {
    return this.cardType === 'user-edit' && this.competency.competencyTypeId === 1001;
  }

  get reason(): string | undefined {
    if (this.isUserCard && this.statusValue === CompetencyStatus.Rejected) {
      return (this.competency as UserCompetency).reason;
    }
    return undefined;
  }

  get rejectedBy(): string | undefined {
    if (this.isUserCard && this.statusValue === CompetencyStatus.Rejected) {
      return (this.competency as UserCompetency).rejectedBy;
    }
    return undefined;
  }

  get isEvidenceRequested(): boolean {
    return this.isUserCard && this.statusValue === CompetencyStatus.DocumentRequested;
  }

  get canRequestEvidence(): boolean {
    if (this.cardType === 'user-draft') {
      return this.statusValue === CompetencyStatus.Unverified && !this.documentFileName;
    }

    if (this.canManageUserEditCard) {
      return (this.statusValue === CompetencyStatus.Unverified && !this.documentFileName)
        || this.statusValue === CompetencyStatus.Expired;
    }

    return false;
  }

  get canUploadEvidence(): boolean {
    return this.allowsSelfServeEvidenceControls
      && this.isEvidenceRequested;
  }

  get isNearExpiry(): boolean {
    if (this.statusValue !== CompetencyStatus.Approved || !this.expiryDate) {
      return false;
    }
    if (typeof this.expiryDate !== 'string') {
      // TODO expiryDate types/model needs simplified to remove the invite-user 'Date' case, so it can be treated as a string always
      throw new Error('CompetencyCardComponent expected approved competency expiryDate to be a date string');
    }
    return DateTimeUtils.isDateStrWithinDays(this.expiryDate, environment.competencySoonToExpireDays);
  }

  // Worker self-view only: allow submitting new evidence on an expired or soon-to-expire competency.
  get canSubmitNewEvidence(): boolean {
    return this.allowsSelfServeEvidenceControls
      && (this.statusValue === CompetencyStatus.Expired || this.isNearExpiry);
  }

  // Worker self-view only: allow resubmitting a document on a rejected competency.
  get canResubmitEvidence(): boolean {
    return this.allowsSelfServeEvidenceControls
      && this.statusValue === CompetencyStatus.Rejected;
  }

  get evidenceState(): EvidenceState {
    if (!this.isUserCard) { return 'none'; }
    if (this.canUploadEvidence) { return 'upload'; }
    if (this.isEvidenceRequested) { return 'requested'; }
    if (this.documentFileName) { return 'document'; }
    if (this.canRequestEvidence) { return 'request'; }
    return 'none';
  }

  get isUserCard(): boolean {
    return this.cardType !== 'work-order-edit' && this.cardType !== 'work-order-readonly';
  }

  get isDraftCard(): boolean {
    return this.cardType === 'user-draft';
  }

  get allowsDeleteControl(): boolean {
    return this.cardType === 'user-edit'
      || this.cardType === 'user-draft'
      || this.cardType === 'work-order-edit';
  }

  get allowsSelfServeEvidenceControls(): boolean {
    return this.cardType === 'user-self';
  }

  get canManageUserEditCard(): boolean {
    return this.cardType === 'user-edit' && this.userCanManageCompetencies;
  }

  private normalizeKey(value: string): string {
    if (!value) { return ''; }
    return value.toLowerCase().replace(/[\s-]+/g, '');
  }

  onApprove(): void {
    if (this.disabled) {
      return;
    }

    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static' };
    const modal = this.modalService.open(AcceptCompetencyModalComponent, modalOptions);
    modal.componentInstance.competency = this.competency;
    modal.componentInstance.siteOwnerId = this.siteOwnerId;
    modal.result.then((result) => {
      if (result === 'refresh') {
        this.updated.emit(this.competency);
      }
    }).catch(() => { /* dismissed */ });
  }

  onReject(): void {
    if (this.disabled) {
      return;
    }

    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static' };
    const modal = this.modalService.open(RejectCompetencyModalComponent, modalOptions);
    modal.componentInstance.competency = this.competency;
    modal.componentInstance.siteOwnerId = this.siteOwnerId;
    modal.result.then((result) => {
      if (result === 'refresh') {
        this.updated.emit(this.competency);
      }
    }).catch(() => { /* dismissed */ });
  }

  onDelete(): void {
    if (this.disabled) {
      return;
    }

    if (this.isDraftCard) {
      this.delete.emit(this.competency);
      return;
    }

    Swal.fire({
      title: 'Delete competency?',
      text: 'Are you sure? This action cannot be reverted!',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.value) {
        this.delete.emit(this.competency);
      }
    });
  }

  onUploadEvidence(): void {
    if (this.disabled || !(this.canUploadEvidence || this.canSubmitNewEvidence || this.canResubmitEvidence)) {
      return;
    }

    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static' };
    const modal = this.modalService.open(UploadEvidenceModalComponent, modalOptions);
    modal.componentInstance.competency = this.competency;
    modal.componentInstance.siteOwnerId = this.siteOwnerId;
    modal.result.then((result) => {
      if (result === 'refresh') {
        this.updated.emit(this.competency);
      }
    }).catch(() => { /* dismissed */ });
  }

  onRequestEvidence(): void {
    if (this.disabled || this.isRequestingEvidence || this.isEvidenceRequested) {
      return;
    }
    // During invite the competency doesn't exist yet, so just record the intent locally and let the
    // parent flag it. The actual request-evidence behaviour runs in competency service after creation.
    if (this.isDraftCard) {
      (this.competency as UserCompetency).status = CompetencyStatus.DocumentRequested;
      this.evidenceRequested.emit(this.competency);
      return;
    }
    const competencyId = (this.competency as UserCompetency).id;
    if (!competencyId || !this.siteOwnerId) {
      return;
    }
    this.isRequestingEvidence = true;
    const self = this;
    this.competencyService.requestEvidence(this.siteOwnerId, competencyId)
      .subscribe(
        function () {
          (self.competency as UserCompetency).status = CompetencyStatus.DocumentRequested;
          self.notificationService.notify('success', 'Evidence requested');
          self.isRequestingEvidence = false;
        },
        function () {
          Swal.fire({ title: 'Error', text: 'Failed to request evidence. Please try again.', type: 'error' });
          self.isRequestingEvidence = false;
        }
      );
  }

  onDocumentClick(event: MouseEvent): void {
    if (this.disabled || !this.documentFileName) {
      return;
    }

    event.preventDefault();
    this.contractorService.showFile(environment.apiUrl + 'api/ext-file/workerUserCompetency', this.documentFileName);
  }
}
